import type { ReactNode } from 'react';

type ProductTourSide = 'top' | 'bottom' | 'left' | 'right';

interface ProductTourStep {
  id: string;
  target: string;
  title: string;
  body: ReactNode;
  side?: ProductTourSide;
  waitForTarget?: boolean;
}

interface ProductTourLabels {
  next: string;
  back: string;
  done: string;
  close: string;
  progress: (current: number, total: number) => string;
}

interface TourPosition {
  stepId: string | undefined;
  index: number;
}

interface SpotlightBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

const TOUR_ACTIVE_ATTRIBUTE = 'data-tour-active';

const SPOTLIGHT_PADDING = 8;

const SPOTLIGHT_RING_WIDTH = 3;

const SPOTLIGHT_MOVE_MS = 300;

const findInitialStepIds = (steps: ProductTourStep[], isPresent: (target: string) => boolean): string[] =>
  steps.reduce<string[]>(
    (acc, step) => ((step.waitForTarget ?? false) || isPresent(step.target) ? [...acc, step.id] : acc),
    []
  );

const tourPositionAt = (steps: ProductTourStep[], index: number): TourPosition => ({
  stepId: steps[index]?.id,
  index,
});

const resolveTourIndex = (steps: ProductTourStep[], { stepId, index }: TourPosition): number => {
  const stepIndex = steps.findIndex((step) => step.id === stepId);
  return stepIndex === -1 ? index : stepIndex;
};

const isSameBox = (first: SpotlightBox | null, second: SpotlightBox | null): boolean =>
  first?.top === second?.top &&
  first?.left === second?.left &&
  first?.width === second?.width &&
  first?.height === second?.height;

const toSpotlightBox = ({ top, left, width, height }: SpotlightBox, padding: number): SpotlightBox => ({
  top: top - padding,
  left: left - padding,
  width: width + 2 * padding,
  height: height + 2 * padding,
});

export {
  findInitialStepIds,
  isSameBox,
  resolveTourIndex,
  SPOTLIGHT_MOVE_MS,
  SPOTLIGHT_PADDING,
  SPOTLIGHT_RING_WIDTH,
  toSpotlightBox,
  TOUR_ACTIVE_ATTRIBUTE,
  tourPositionAt,
};
export type { ProductTourLabels, ProductTourSide, ProductTourStep, SpotlightBox, TourPosition };
